<template>
    <!--
        Account Settings page. Lists Twitch + Discord linked-state for the
        currently-authenticated User and surfaces:
          - "Link <provider>" CTA for unlinked providers (top-level OAuth nav)
          - "Connect ChatFight to your Discord server" placeholder (4.6 wires)
          - Status banners driven by `?linked=` / `?error=` query params

        Vuetify v3 components match the rest of the SPA. Brand colors mirror
        LoginPageComponent so the link buttons look like the login buttons.
    -->
    <v-container fluid class="account-settings-container">
        <v-row v-if="banner.message" class="banner-row">
            <v-col cols="12">
                <v-alert
                    :type="banner.type"
                    :text="banner.message"
                    closable
                    @click:close="banner.message = ''"
                ></v-alert>
            </v-col>
        </v-row>

        <v-row>
            <v-col cols="12">
                <h1 class="account-settings-title">Account Settings</h1>
            </v-col>
        </v-row>

        <v-overlay v-model="loading" class="align-center justify-center" absolute>
            <v-progress-circular
                color="white"
                indeterminate
                size="64"
            ></v-progress-circular>
        </v-overlay>

        <v-row v-if="!loading">
            <v-col cols="12" md="8" offset-md="2">
                <v-card class="provider-card">
                    <v-card-title>Linked Accounts</v-card-title>
                    <v-card-text>
                        <!--
                            Twitch row. Linked → username + linked_at + badge.
                            Unlinked → "Link Twitch" button that navigates the
                            browser (not a fetch) to /auth/twitch?action=link.
                            Top-level OAuth nav has to be a full navigation so
                            the server can set the cf_session cookie on the
                            redirect's response.
                        -->
                        <div class="provider-row">
                            <div class="provider-info">
                                <span class="provider-name">Twitch</span>
                                <template v-if="settings.providers.twitch">
                                    <span class="provider-username">
                                        @{{ settings.providers.twitch.username }}
                                    </span>
                                    <span class="provider-linked-at">
                                        Linked {{ formatDate(settings.providers.twitch.linked_at) }}
                                    </span>
                                    <v-chip class="linked-badge" color="success" size="small">Linked</v-chip>
                                </template>
                                <span v-else class="provider-unlinked">Not linked</span>
                            </div>
                            <a
                                v-if="!settings.providers.twitch"
                                href="/auth/twitch?action=link"
                                class="twitch-link-btn"
                            >Link Twitch</a>
                        </div>

                        <v-divider class="my-4"></v-divider>

                        <div class="provider-row">
                            <div class="provider-info">
                                <span class="provider-name">Discord</span>
                                <template v-if="settings.providers.discord">
                                    <span class="provider-username">
                                        {{ settings.providers.discord.global_name
                                            ?? settings.providers.discord.username }}
                                    </span>
                                    <span class="provider-linked-at">
                                        Linked {{ formatDate(settings.providers.discord.linked_at) }}
                                    </span>
                                    <v-chip class="linked-badge" color="success" size="small">Linked</v-chip>
                                </template>
                                <span v-else class="provider-unlinked">Not linked</span>
                            </div>
                            <a
                                v-if="!settings.providers.discord"
                                href="/auth/discord?action=link"
                                class="discord-link-btn"
                            >Link Discord</a>
                        </div>
                    </v-card-text>
                </v-card>

                <!--
                    5.7 device-pairing entry point. The Unity client has no
                    cookie-bound browser session, so we broker the handshake
                    via a single-use 8-character code: the SPA generates it
                    while the user is logged in (cf_session cookie auth);
                    the streamer types it into Unity, which exchanges the
                    code for a 30-day bearer JWT. Codes carry a 5-minute TTL,
                    bound to (user_id, via_provider) by the server, and are
                    consumed atomically on first exchange.
                -->
                <v-card class="unity-pair-card mt-4">
                    <v-card-title>Connect Unity to your account</v-card-title>
                    <v-card-text>
                        <p class="mb-3">
                            Paste this code into the ChatFight Unity app to
                            pair it with your account. Codes are single-use
                            and expire after 5 minutes.
                        </p>
                        <v-btn
                            v-if="!unityPairing.code"
                            color="#995BD5"
                            class="text-white"
                            :loading="unityPairing.generating"
                            @click="generateUnityPairCode"
                        >Generate pairing code</v-btn>

                        <div
                            v-if="unityPairing.code"
                            class="unity-pair-result"
                        >
                            <code class="unity-pair-code">{{ unityPairing.code }}</code>
                            <v-btn
                                size="small"
                                variant="outlined"
                                color="white"
                                @click="copyUnityPairCode"
                            >{{ unityPairing.copied ? 'Copied!' : 'Copy' }}</v-btn>
                            <span class="unity-pair-countdown">
                                Expires in {{ unityPairing.secondsLeft }}s
                            </span>
                        </div>

                        <p
                            v-if="unityPairing.expired"
                            class="unity-pair-expired mt-2"
                        >
                            Code expired. Generate a new one.
                        </p>
                        <p
                            v-if="unityPairing.error"
                            class="unity-pair-error mt-2"
                        >{{ unityPairing.error }}</p>
                    </v-card-text>
                </v-card>

                <!--
                    Bot-install entry point (4.6). The install button is
                    ALWAYS enabled — a streamer with the bot already in one
                    guild can install it in additional guilds. The per-row
                    list below shows what's already configured with "Edit
                    channels" + "Test bot connection" actions.

                    Bot install is intentionally INDEPENDENT of whether the
                    user has linked Discord via OAuth login (the "Link
                    Discord" provider button above). The OAuth login flow
                    and the bot install flow are separate OAuth grants;
                    streamers commonly install the bot before linking, or
                    use a different Discord account for their bot than for
                    their personal Discord login. Per the design plan,
                    don't gate one on the other.
                -->
                <v-card class="bot-install-card mt-4">
                    <v-card-title>Discord Server Integration</v-card-title>
                    <v-card-text>
                        <p class="mb-3">
                            Add the ChatFight bot to your Discord server so
                            viewers can join battles from chat.
                        </p>
                        <v-btn
                            color="#5865F2"
                            class="text-white"
                            @click="installBot"
                        >Connect ChatFight to your Discord server</v-btn>
                        <p class="bot-install-tip mt-2">
                            Tip: If you don't get redirected back, make sure
                            you have <strong>Manage Server</strong>
                            permission in the target guild.
                        </p>

                        <div
                            v-if="settings.discord_guild_configs.length > 0"
                            class="mt-4"
                        >
                            <h3 class="installed-guilds-heading">Installed guilds</h3>
                            <div
                                v-for="cfg in settings.discord_guild_configs"
                                :key="cfg.guild_id"
                                class="guild-row mt-2"
                            >
                                <div class="guild-info">
                                    <div>
                                        <span class="guild-id">Guild {{ cfg.guild_name || cfg.guild_id }}</span>
                                        <span class="guild-installed-at">
                                            (installed {{ formatDate(cfg.installed_at) }})
                                        </span>
                                    </div>
                                    <div class="guild-channels">
                                        Channels:
                                        <span
                                            v-if="cfg.enabled_channels.length === 0"
                                            class="no-channels"
                                        >none picked</span>
                                        <code
                                            v-for="ch in cfg.enabled_channels"
                                            :key="ch.channel_id"
                                            class="channel-id-pill"
                                        >#{{ ch.channel_name || ch.channel_id }}</code>
                                    </div>
                                    <div class="guild-test-result" v-if="testResults[cfg.guild_id]">
                                        {{ testResults[cfg.guild_id] }}
                                    </div>
                                </div>
                                <div class="guild-actions">
                                    <v-btn
                                        size="small"
                                        variant="outlined"
                                        color="white"
                                        @click="editChannels(cfg.guild_id)"
                                    >Edit channels</v-btn>
                                    <v-btn
                                        size="small"
                                        variant="outlined"
                                        color="white"
                                        :loading="testingGuildId === cfg.guild_id"
                                        @click="testBotConnection(cfg.guild_id)"
                                    >Test bot connection</v-btn>
                                </div>
                            </div>
                        </div>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <!--
            2.6 merge confirmation modal. Renders only when the OAuth
            callback bounced back with `?merge_required=1&merge_id=<nonce>`.
            The modal does its own fetch on mount and handles confirm /
            cancel; this parent just owns the show/hide flag and the
            success-redirect.
        -->
        <MergeConfirmModal
            v-if="mergeRequired.merge_id"
            v-model="mergeRequired.open"
            :merge-id="mergeRequired.merge_id"
            :provider="mergeRequired.provider"
            @merged="onMergeConfirmed"
            @cancelled="onMergeCancelled"
            @active-session="onMergeActiveSession"
        />
    </v-container>
</template>

<script>
import MergeConfirmModal from './MergeConfirmModal.vue';

export default {
    components: { MergeConfirmModal },
    data() {
        return {
            loading: true,
            // Defaults render an "empty" page during load — same shape as the
            // server response so the template doesn't need ?. guards on every
            // field. The watcher in mounted() replaces it with real data.
            settings: {
                providers: {},
                discord_guild_configs: [],
            },
            banner: {
                type: 'info',
                message: '',
            },
            // Per-guild_id → human-readable test result. Populated on click
            // of "Test bot connection"; one row per installed guild.
            testResults: {},
            // The guild_id currently being tested (drives the per-button
            // loading spinner). Null when no test is in flight.
            testingGuildId: null,
            // 2.6 merge-confirmation modal state. `merge_id` is the opaque
            // nonce from `?merge_required=1&merge_id=...`; `provider` drives
            // the modal's "Discord"/"Twitch" copy; `open` is the v-dialog
            // model. Empty `merge_id` ⇒ no modal mounted at all.
            mergeRequired: {
                open: false,
                merge_id: '',
                provider: 'discord',
            },
            // 5.7 Unity device-pairing state. Empty `code` ⇒ "Generate"
            // button rendered; non-empty ⇒ code + countdown + Copy.
            // `secondsLeft` ticks via `_unityPairTicker` (cleared in
            // beforeUnmount so the interval doesn't leak across route
            // changes). When it reaches 0 we clear the code locally — the
            // server-side row may still exist briefly, but the user can't
            // act on it past the countdown.
            unityPairing: {
                generating: false,
                code: '',
                expiresAt: 0,
                secondsLeft: 0,
                copied: false,
                expired: false,
                error: '',
            },
        };
    },
    async mounted() {
        // Surface ?linked= and ?error= banners before/while fetching so the
        // user sees feedback on returning from an OAuth round-trip without
        // waiting on the network round-trip.
        const query = this.$route?.query ?? {};
        if (query.linked === 'twitch') {
            this.banner = { type: 'success', message: 'Twitch account linked!' };
        } else if (query.linked === 'discord') {
            this.banner = { type: 'success', message: 'Discord account linked!' };
        } else if (query.merge_required === '1' && typeof query.merge_id === 'string') {
            // 2.6 entry point. The OAuth callback redirected here because the
            // provider id is already owned by a different ChatFight account
            // that has data — the modal handles the diff + confirm flow.
            const provider = query.provider === 'twitch' ? 'twitch' : 'discord';
            this.mergeRequired = {
                open: true,
                merge_id: String(query.merge_id),
                provider,
            };
        } else if (query.error === 'active_session') {
            // Surfaced by linkWith*'s ACTIVE_SESSION translation. Distinct
            // from merge_required so the host can stop the battle and retry.
            this.banner = {
                type: 'warning',
                message: 'Cannot link while a battle is in progress. End the battle and try again.',
            };
        } else if (query.error === 'oauth_state') {
            this.banner = {
                type: 'error',
                message: 'Login session expired. Try again.',
            };
        } else if (query.error === 'link_flow_pending') {
            // Emitted by discordSetup's pre-guard when a logged-in user hits
            // /auth/discord without ?action=link (legacy path; the Link
            // button on this page uses ?action=link explicitly).
            this.banner = {
                type: 'info',
                message: 'You are already logged in. Use the Link Discord button below to attach another provider.',
            };
        }

        try {
            const response = await fetch('/users/me/account-settings', {
                method: 'GET',
                credentials: 'include', // cf_session cookie carries auth
            });
            if (response.status === 401) {
                // Auth dropped mid-session — push back to login.
                this.$router?.replace('/login');
                return;
            }
            if (!response.ok) {
                throw new Error(`account-settings: HTTP ${response.status}`);
            }
            const data = await response.json();
            this.settings = data;
        } catch (err) {
            console.error('AccountSettings: fetch failed', err);
            this.banner = {
                type: 'error',
                message: 'Could not load account settings. Please try again.',
            };
        } finally {
            this.loading = false;
        }
    },
    methods: {
        // Top-level OAuth nav must be a full page navigation so the server
        // can read the cf_session cookie on the GET. A fetch() with
        // credentials would set the cookie on a fetch response that the
        // SPA can't act on; only `window.location` lets the server-issued
        // redirect to Discord propagate to the browser.
        installBot() {
            window.location.href = '/api/discord/install/start';
        },
        editChannels(guildId) {
            // The picker route requires a parked user OAuth token in
            // BotInstallTokenStore. If the user clicks "Edit channels"
            // without re-running the install flow, the picker will 410 and
            // bounce them back. That's acceptable: re-installing is the
            // recovery path. A future task can capture refresh tokens to
            // re-use without re-prompting.
            this.$router.push({
                path: '/account/discord/channels',
                query: { guild_id: guildId },
            });
        },
        async testBotConnection(guildId) {
            this.testingGuildId = guildId;
            this.testResults[guildId] = '';
            try {
                const res = await fetch(
                    `/api/discord/test-connection?guild_id=${encodeURIComponent(guildId)}`,
                    { method: 'GET', credentials: 'include' },
                );
                if (!res.ok) {
                    this.testResults[guildId] = `Test failed (HTTP ${res.status})`;
                    return;
                }
                const data = await res.json();
                if (data.in_guild) {
                    this.testResults[guildId] = `Bot is in ${data.guild_name ?? guildId}.`;
                } else {
                    this.testResults[guildId] =
                        `Bot is not in ${data.guild_id ?? guildId} anymore — re-install?`;
                }
            } catch (err) {
                console.error('testBotConnection failed', err);
                this.testResults[guildId] = 'Test failed. Try again in a moment.';
            } finally {
                this.testingGuildId = null;
            }
        },
        formatDate(value) {
            if (!value) return '';
            const d = new Date(value);
            if (Number.isNaN(d.getTime())) return '';
            // Locale-default ISO-ish format. Display polish lives in a
            // shared util when 2.7+ adds more date surfaces.
            return d.toLocaleDateString();
        },
        // ── 2.6 merge-modal callbacks ──────────────────────────────────
        onMergeConfirmed({ provider }) {
            // Full nav (not router.push) — the server-side merge changes
            // session-shape state that App.vue's mount-time /users/me
            // hydration needs to re-read. The redirect also strips the
            // merge_required query string so a reload doesn't try to
            // re-confirm an already-consumed nonce.
            window.location.href = `/account-settings?linked=${encodeURIComponent(provider)}`;
        },
        onMergeCancelled() {
            // Tear the modal down and clear the merge_id so the v-if
            // unmounts cleanly. router.replace drops the query string from
            // history so a back-navigation doesn't reopen the modal with a
            // now-deleted merge_id.
            this.mergeRequired = { open: false, merge_id: '', provider: 'discord' };
            this.banner = {
                type: 'info',
                message: 'Accounts kept separate. You can re-link any time.',
            };
            this.$router?.replace({ path: '/account-settings' });
        },
        onMergeActiveSession() {
            // The 409 response from confirm-merge means the host has a
            // battle in flight. Don't tear the modal down — the user can
            // retry once the battle ends. The modal renders its own error.
        },
        // ── 5.7 Unity device-pairing ──────────────────────────────────
        async generateUnityPairCode() {
            this.unityPairing.error = '';
            this.unityPairing.expired = false;
            this.unityPairing.generating = true;
            try {
                const res = await fetch('/api/auth/generate-pair-code', {
                    method: 'POST',
                    credentials: 'include',
                });
                if (res.status === 401) {
                    this.$router?.replace('/login');
                    return;
                }
                if (!res.ok) {
                    throw new Error(`generate-pair-code: HTTP ${res.status}`);
                }
                const data = await res.json();
                if (typeof data.code !== 'string' || !data.code) {
                    throw new Error('generate-pair-code: empty code in response');
                }
                this.unityPairing.code = data.code;
                this.unityPairing.copied = false;
                // 5-minute TTL matches the server constant; we drive a local
                // countdown so the user sees urgency. If the server changes
                // the TTL, this number drifts but only visually — the
                // server-side TTL stays authoritative.
                this.unityPairing.expiresAt = Date.now() + 5 * 60 * 1000;
                this.unityPairing.secondsLeft = 5 * 60;
                this.startUnityPairCountdown();
            } catch (err) {
                console.error('generateUnityPairCode failed', err);
                this.unityPairing.error =
                    'Could not generate code. Please try again in a moment.';
            } finally {
                this.unityPairing.generating = false;
            }
        },
        startUnityPairCountdown() {
            this.stopUnityPairCountdown();
            this._unityPairTicker = setInterval(() => {
                const remainingMs = this.unityPairing.expiresAt - Date.now();
                if (remainingMs <= 0) {
                    this.unityPairing.code = '';
                    this.unityPairing.secondsLeft = 0;
                    this.unityPairing.expired = true;
                    this.unityPairing.copied = false;
                    this.stopUnityPairCountdown();
                    return;
                }
                this.unityPairing.secondsLeft = Math.ceil(remainingMs / 1000);
            }, 1000);
        },
        stopUnityPairCountdown() {
            if (this._unityPairTicker) {
                clearInterval(this._unityPairTicker);
                this._unityPairTicker = null;
            }
        },
        async copyUnityPairCode() {
            const code = this.unityPairing.code;
            if (!code) return;
            try {
                if (navigator.clipboard && navigator.clipboard.writeText) {
                    await navigator.clipboard.writeText(code);
                } else {
                    // Older browsers / non-secure contexts — fall back to a
                    // hidden textarea. The streamer can still re-type the
                    // 8-char code by hand if even this fails.
                    const ta = document.createElement('textarea');
                    ta.value = code;
                    ta.style.position = 'fixed';
                    ta.style.opacity = '0';
                    document.body.appendChild(ta);
                    ta.select();
                    document.execCommand('copy');
                    document.body.removeChild(ta);
                }
                this.unityPairing.copied = true;
                setTimeout(() => {
                    this.unityPairing.copied = false;
                }, 2000);
            } catch (err) {
                console.error('copyUnityPairCode failed', err);
                this.unityPairing.error =
                    'Clipboard copy failed — type the code manually.';
            }
        },
    },
    beforeUnmount() {
        this.stopUnityPairCountdown();
    },
};
</script>

<style scoped>
.account-settings-container {
    padding: 30px;
    padding-top: 80px;
    color: white;
}
.account-settings-title {
    color: white;
    text-align: left;
}
.provider-card,
.bot-install-card,
.unity-pair-card {
    background-color: rgba(0, 0, 0, 0.5) !important;
    color: white !important;
}
/*
 * Vuetify v3 (no theme configured → light theme) applies
 * `color: rgba(var(--v-theme-on-surface), …)` directly to .v-card-title,
 * .v-card-text, and .v-card-subtitle, which overrides inheritance from
 * the parent card's `color: white`. Force white at the deep level on
 * every text container we render.
 */
.provider-card :deep(.v-card-title),
.provider-card :deep(.v-card-text),
.provider-card :deep(.v-card-subtitle),
.bot-install-card :deep(.v-card-title),
.bot-install-card :deep(.v-card-text),
.bot-install-card :deep(.v-card-subtitle),
.unity-pair-card :deep(.v-card-title),
.unity-pair-card :deep(.v-card-text),
.unity-pair-card :deep(.v-card-subtitle) {
    color: white !important;
}
.provider-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
}
.provider-info {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
}
.provider-name {
    font-weight: 600;
    min-width: 80px;
}
.provider-username {
    font-family: monospace;
}
.provider-linked-at {
    opacity: 0.8;
    font-size: 0.9em;
}
.provider-unlinked {
    opacity: 0.7;
    font-style: italic;
}
.linked-badge {
    margin-left: 8px;
}
/*
 * Brand colors mirror LoginPageComponent's twitch / discord buttons so the
 * link-flow CTAs look like the login-flow CTAs. Kept inline (not extracted
 * to a shared SCSS module) because the brand palette across the SPA hasn't
 * been factored yet — a wider color-palette refactor is a separate task.
 */
.twitch-link-btn,
.discord-link-btn {
    width: 160px;
    height: 36px;
    color: white;
    border-radius: 4px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    text-decoration: none;
}
.twitch-link-btn {
    background-color: #995BD5;
}
.twitch-link-btn:hover {
    background-color: #8849c4;
}
.discord-link-btn {
    background-color: #5865F2;
}
.discord-link-btn:hover {
    background-color: #4752c4;
}
.bot-install-tip {
    color: #cccccc;
    font-size: 0.85em;
    opacity: 0.85;
}
.installed-guilds-heading {
    color: white;
    font-size: 1.1em;
    margin-bottom: 8px;
}
.guild-row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
    padding: 8px 0;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
}
.guild-info {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 4px;
}
.guild-id {
    font-family: monospace;
    font-weight: 600;
}
.guild-installed-at {
    opacity: 0.7;
    font-size: 0.85em;
    margin-left: 8px;
}
.guild-channels {
    font-size: 0.9em;
}
.channel-id-pill {
    background-color: rgba(255, 255, 255, 0.1);
    padding: 2px 6px;
    border-radius: 3px;
    margin: 0 4px;
    font-family: monospace;
    font-size: 0.85em;
}
.no-channels {
    opacity: 0.6;
    font-style: italic;
}
.guild-test-result {
    font-size: 0.9em;
    color: #FFD479;
    margin-top: 4px;
}
.guild-actions {
    display: flex;
    flex-direction: column;
    gap: 6px;
}
.banner-row {
    margin-top: 16px;
}
.unity-pair-result {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
}
.unity-pair-code {
    font-family: monospace;
    font-size: 1.6em;
    letter-spacing: 0.12em;
    background-color: rgba(255, 255, 255, 0.1);
    padding: 6px 14px;
    border-radius: 4px;
    color: white;
}
.unity-pair-countdown {
    opacity: 0.8;
    font-size: 0.9em;
}
.unity-pair-expired {
    color: #FFD479;
    font-size: 0.9em;
}
.unity-pair-error {
    color: #ff9090;
    font-size: 0.9em;
}
</style>
